import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Alert,
  CircularProgress,
  Stack,
} from '@mui/material';
import {
  Visibility as ViewIcon,
  Delete as DeleteIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
} from '@mui/icons-material';
import apiClient from '../../services/api';

const ManageContactMessages = () => {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [selected, setSelected] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);

  const loadMessages = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const result = await apiClient.getContactSubmissions();
      if (result && result.success) {
        setMessages(result.data);
      } else {
        setError(result?.error || 'Failed to load messages');
      }
    } catch (e) {
      setError(e.message || 'Failed to load messages');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadMessages();
  }, [loadMessages]);

  const handleView = async (message) => {
    setSelected(message);
    setDialogOpen(true);
    if (message.status !== 'read') {
      try {
        const result = await apiClient.updateContactSubmissionStatus(message.id, 'read');
        if (result && result.success) {
          setMessages((prev) => prev.map((m) => (m.id === message.id ? { ...m, status: 'read' } : m)));
        }
      } catch (e) {
        // Non-critical - the message still opens even if marking it read fails.
      }
    }
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelected(null);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this message?')) return;
    try {
      const result = await apiClient.deleteContactSubmission(id);
      if (result && result.success) {
        await loadMessages();
      } else {
        setError(result?.error || 'Failed to delete message');
      }
    } catch (e) {
      setError(e.message || 'Failed to delete message');
    }
  };

  const unreadCount = messages.filter((m) => m.status !== 'read').length;

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1">
          Contact Messages
          {unreadCount > 0 && (
            <Chip label={`${unreadCount} new`} color="primary" size="small" sx={{ ml: 2 }} />
          )}
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Status</TableCell>
                <TableCell>Name</TableCell>
                <TableCell>Subject</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Date</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {messages.map((message) => (
                <TableRow key={message.id} sx={{ bgcolor: message.status !== 'read' ? 'action.hover' : undefined }}>
                  <TableCell>
                    <Chip
                      label={message.status === 'read' ? 'Read' : 'New'}
                      color={message.status === 'read' ? 'default' : 'primary'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="subtitle2" sx={{ fontWeight: message.status !== 'read' ? 'bold' : 'normal' }}>
                      {message.name}
                    </Typography>
                  </TableCell>
                  <TableCell>{message.subject}</TableCell>
                  <TableCell>{message.email}</TableCell>
                  <TableCell>{new Date(message.created_at).toLocaleDateString()}</TableCell>
                  <TableCell>
                    <IconButton size="small" onClick={() => handleView(message)} color="primary">
                      <ViewIcon />
                    </IconButton>
                    <IconButton size="small" onClick={() => handleDelete(message.id)} color="error">
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
              {messages.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6}>
                    <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
                      No messages yet.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        {selected && (
          <>
            <DialogTitle>{selected.subject}</DialogTitle>
            <DialogContent>
              <Stack spacing={2}>
                <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                  {selected.name}
                </Typography>
                <Stack direction="row" spacing={1} alignItems="center">
                  <EmailIcon fontSize="small" color="action" />
                  <Typography variant="body2">{selected.email}</Typography>
                </Stack>
                {selected.phone && (
                  <Stack direction="row" spacing={1} alignItems="center">
                    <PhoneIcon fontSize="small" color="action" />
                    <Typography variant="body2">{selected.phone}</Typography>
                  </Stack>
                )}
                <Typography variant="body2" color="text.secondary">
                  {new Date(selected.created_at).toLocaleString()}
                </Typography>
                <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                  {selected.message}
                </Typography>
              </Stack>
            </DialogContent>
            <DialogActions>
              <Button
                component="a"
                href={`mailto:${selected.email}?subject=${encodeURIComponent(`Re: ${selected.subject}`)}`}
              >
                Reply by Email
              </Button>
              <Button onClick={handleCloseDialog} variant="contained">
                Close
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </Box>
  );
};

export default ManageContactMessages;
